import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { DataContext } from '@visx/xychart';

import { useChartContext } from '../components/ChartContext';
import { useTooltipContext } from '../components/Tooltip';
import { ZOOM_MIN_DRAG_DISTANCE_PX } from '../constants';
import { createDomainCalculator } from '../internal/utils/dragToZoom/domainCalculatorFactory';
import { SelectionRect } from '../internal/utils/dragToZoom/SelectionRect';
import type { DomainCalculatorContext } from '../internal/utils/dragToZoom/types';
import { useDragState } from '../internal/utils/dragToZoom/useDragState';

export interface UseDragToZoomReturn {
  selectionRect: React.ReactNode | null;
  eventHandlers: {
    onMouseDown: (event: React.MouseEvent<SVGSVGElement>) => void;
    onMouseMove: (event: React.MouseEvent<SVGSVGElement>) => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
  };
  // Alternative handlers that accept coordinates directly (for use with visx event emitter)
  coordinateHandlers: {
    onPointerDown: (svgPoint: { x: number; y: number }) => void;
    onPointerMove: (svgPoint: { x: number; y: number }) => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
  };
  zoomDomain: [number, number] | string[] | null;
}

/**
 * Hook for drag-to-zoom functionality in charts.
 * Must be called inside both XYChart (for DataContext) and TooltipProvider (for TooltipContext).
 *
 * @returns Object with selection rectangle, reset button, event handlers, and zoom domain
 */
export function useDragToZoom(): UseDragToZoomReturn {
  const chartContext = useChartContext();
  const tooltipContext = useTooltipContext();
  const dataContext = useContext(DataContext);

  const { disableDragToZoom, zoomDomain, setZoomDomain, xDataKey, chartType, xScaleType, chartData } = chartContext;
  const { setActiveXValue, unpinTooltip } = tooltipContext;
  const { xScale, innerHeight, margin } = dataContext || {};

  // Handle mouse up with domain calculation
  // Receives coordinates directly from useDragState to avoid stale ref issues
  const handleMouseUp = useCallback(
    (selectionStartX: number | null, selectionEndX: number | null) => {
      if (disableDragToZoom || !xScale || selectionStartX === null || selectionEndX === null) {
        return;
      }

      const dragDistance = Math.abs(selectionEndX - selectionStartX);

      if (dragDistance >= ZOOM_MIN_DRAG_DISTANCE_PX) {
        try {
          // Create domain calculator based on scale type
          const calculator = createDomainCalculator(xScale, xScaleType);

          // Prepare context for calculator
          const context: DomainCalculatorContext = {
            xDataKey,
            chartType,
            xScaleType,
            zoomDomain,
            chartData: chartData ?? [],
            setZoomDomain,
          };

          // Calculate domain using the appropriate calculator
          const calculatedDomain = calculator.calculateDomain(selectionStartX, selectionEndX, xScale, context);

          // Set zoom domain if calculation succeeded
          if (calculatedDomain !== null) {
            setZoomDomain(calculatedDomain);
          }
        } catch {
          // Silently fail if scale operations throw (e.g., invalid coordinates)
          // Error is caught to prevent component crash, but not logged to avoid console noise
        }
      }
    },
    [disableDragToZoom, xScale, chartType, xScaleType, xDataKey, zoomDomain, chartData, setZoomDomain],
  );

  // Callback when drag starts - hide active tooltip and unpin any pinned tooltip
  const handleDragStart = useCallback(() => {
    setActiveXValue(null);
    unpinTooltip();
  }, [setActiveXValue, unpinTooltip]);

  // Use extracted drag state hook with onDragEnd callback for window event handling
  const {
    isDragging,
    selectionStartX,
    selectionEndX,
    handleMouseDown,
    handleMouseMove,
    handleMouseLeave,
    handlePointerDownWithCoords,
    handlePointerMoveWithCoords,
    resetDragState,
  } = useDragState(disableDragToZoom, xScale, handleDragStart, (startX, endX) => {
    handleMouseUp(startX, endX);
    resetDragStateRef.current?.();
  });

  // Ref for resetDragState to avoid circular dependency
  const resetDragStateRef = useRef(resetDragState);
  useEffect(() => {
    resetDragStateRef.current = resetDragState;
  }, [resetDragState]);

  // Combined handler that calculates domain and resets state (for direct mouse events on chart)
  const handleMouseUpAndReset = useCallback(() => {
    handleMouseUp(selectionStartX, selectionEndX);
    resetDragState();
  }, [handleMouseUp, selectionStartX, selectionEndX, resetDragState]);

  // Render selection rectangle while dragging
  const selectionRect = useMemo(() => {
    return (
      <SelectionRect
        disableDragToZoom={disableDragToZoom}
        isDragging={isDragging}
        selectionStartX={selectionStartX}
        selectionEndX={selectionEndX}
        innerHeight={innerHeight}
        margin={margin}
      />
    );
  }, [disableDragToZoom, isDragging, selectionStartX, selectionEndX, innerHeight, margin]);

  return {
    selectionRect,
    eventHandlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUpAndReset,
      onMouseLeave: handleMouseLeave,
    },
    coordinateHandlers: {
      onPointerDown: handlePointerDownWithCoords,
      onPointerMove: handlePointerMoveWithCoords,
      onPointerUp: handleMouseUpAndReset,
      onPointerLeave: handleMouseLeave,
    },
    zoomDomain,
  };
}
